import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Mail, Phone, Trash2, Globe, LayoutDashboard, FileSignature, Loader2 } from "lucide-react";

const statusMeta = {
  new: { label: "New", className: "bg-blue-100 text-blue-700" },
  contacted: { label: "Contacted", className: "bg-amber-100 text-amber-700" },
  won: { label: "Won", className: "bg-emerald-100 text-emerald-700" },
  lost: { label: "Lost", className: "bg-rose-100 text-rose-700" },
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
  }, []);

  const sendAgreement = async (lead) => {
    setBusy(`agreement-${lead.id}`);
    try {
      const { data } = await api.post(`/leads/${lead.id}/contract`);
      const link = `${window.location.origin}/contract/${data.public_token}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Agreement created — signing link copied to clipboard!");
      } catch {
        toast.success("Agreement created. Find it under Contracts.");
      }
      setLeads((ls) => ls.map((l) => (l.id === lead.id && l.status === "new" ? { ...l, status: "contacted" } : l)));
    } catch (e) {
      toast.error("Could not create agreement");
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/leads/${id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch (e) {
      toast.error("Could not update lead");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/leads/${id}`);
      toast.success("Lead deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error("Could not delete lead");
    }
  };

  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-primary mb-1">Sales</p>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Leads</h1>
        <p className="text-muted-foreground text-sm mt-1">
          People who requested an estimate quote. {newCount} new.
        </p>
      </div>

      {leads.length === 0 ? (
        <Card className="p-12 text-center" data-testid="leads-empty">
          <UserPlus className="h-10 w-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.3} />
          <p className="text-muted-foreground">
            No leads yet. Share your estimate calculator to start capturing prospects.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => {
            const meta = statusMeta[l.status] || statusMeta.new;
            return (
              <Card key={l.id} className="p-5" data-testid={`lead-row-${l.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-display font-semibold">{l.name || "Unnamed lead"}</span>
                      <Badge className={`${meta.className} border-0`}>{meta.label}</Badge>
                      <Badge variant="outline" className="gap-1 text-muted-foreground">
                        {l.source === "public" ? <Globe className="h-3 w-3" /> : <LayoutDashboard className="h-3 w-3" />}
                        {l.source === "public" ? "Website" : "Dashboard"}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-primary">
                        <Mail className="h-3.5 w-3.5" /> {l.email}
                      </a>
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                          <Phone className="h-3.5 w-3.5" /> {l.phone}
                        </a>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {l.city}, {l.state} · {l.frequency_label} · {l.bedrooms}bd / {l.bathrooms}ba · {l.sqft} sq ft
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display text-xl font-semibold">${Math.round(l.typical)}</p>
                    <p className="text-xs text-muted-foreground">
                      est. ${Math.round(l.low)}–${Math.round(l.high)}
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => sendAgreement(l)}
                      disabled={busy === `agreement-${l.id}`}
                      data-testid={`send-agreement-${l.id}`}
                    >
                      {busy === `agreement-${l.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <FileSignature className="h-4 w-4" />
                      )}
                      <span className="ml-1 hidden sm:inline">Send agreement</span>
                    </Button>
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger className="w-36" data-testid={`lead-status-${l.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="contacted">Contacted</SelectItem>
                        <SelectItem value="won">Won</SelectItem>
                        <SelectItem value="lost">Lost</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteTarget(l)}
                      data-testid={`delete-lead-${l.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name || deleteTarget?.email}" will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-lead">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => remove(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-lead"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
